import { AddressInfoTypeEnum, query } from 'api';
import { Wrapper } from 'components/BaseLayout/components/Wrapper';
import Chat from 'components/Chat/Chat';
import { IDimeloIdentity } from 'components/Chat/chat.utils';
import { ChildPage } from 'components/ChildPage';
import { PageHeader } from 'components/PageHeader';
import { AddressesContext } from 'context/addresses.context';
import { BillingCustomerContext } from 'context/billing-customer.context';
import { gql } from 'graphql-request';
import { useContactDetailsContext } from 'pages/ContactDetails/contact-details.context';
import React, { useContext, useState } from 'react';
import { ContentUtil } from 'utils';
import { Helmet } from 'react-helmet';

import content from './messenger.content';
import * as S from './messenger.styles';

const contentUtil = new ContentUtil({ content, componentName: 'Messenger' });

interface IIdentityValues {
  customerId?: string;
  customerName?: string;
  ctn?: string | null;
  emailAddress?: string | null;
  street?: string | null;
  city?: string;
  postCode?: string;
  fixedNumber?: string | null;
  huisnummer?: number | null;
  huisnummer_toev?: string | null;
  gdpr_check: boolean;
}

export const MessengerContainer = () => {
  const { contractsAndContacts } = useContext(BillingCustomerContext);
  const [token, setToken] = useState<string | undefined>(undefined);
  const { contactDetails: contact } = useContactDetailsContext();
  const { addresses } = useContext(AddressesContext);

  const setTokenViaSignMessenger = async (
    identity: IDimeloIdentity,
    identityExtraValues: IIdentityValues
  ): Promise<void> => {
    if (!identity || !identityExtraValues || token) return;

    const result = await query({
      query: gql`
        query signMessenger($extraValues: SignMessengerExtraValues!, $payload: SignMessengerPayload!) {
          signMessenger(payload: $payload, extraValues: $extraValues) {
            token
          }
        }
      `,
      name: 'signMessenger',
      variables: {
        payload: identity,
        extraValues: identityExtraValues,
      },
    });

    setToken(result.signMessenger?.token);
  };

  if (!contractsAndContacts) return null;

  const identity = {
    screenname: contractsAndContacts.contact.full_name,
    firstname: contractsAndContacts.contact.first_name,
    lastname: contractsAndContacts.contact.last_name,
    uuid: contractsAndContacts.billing_customer.billing_customer_id,
  };

  const address = addresses?.find((address) => address.type === AddressInfoTypeEnum.ASSIGNEDPRODUCT)?.address;
  const identityExtraValues = {
    customerId: contact?.id,
    customerName: contact?.primaryContact.lastName,
    ctn: contact?.primaryContact.mobile,
    emailAddress: contact?.primaryContact.email,
    street: address?.streetName,
    city: address?.city,
    postCode: address?.postCode,
    fixedNumber: contact?.primaryContact.phone,
    huisnummer: address?.houseNumber,
    huisnummer_toev: address?.houseNumberExtension,
    gdpr_check: true,
  };

  if (!contact || !address || !contractsAndContacts) {
    return null;
  }

  if (!token) {
    setTokenViaSignMessenger(identity, identityExtraValues);
    return null;
  }

  return (
    <S.MessengerContainer>
      <Chat
        fullPage
        token={token}
        customerType={contractsAndContacts?.billing_customer?.is_consumer ? 'consumer' : 'business'}
        identity={identity}
        extraIdentityValues={identityExtraValues}
      />
    </S.MessengerContainer>
  );
};

const Messenger = () => {
  return (
    <ChildPage>
      <Helmet>
        <title>{contentUtil.translateToString('pageTitle')}</title>
      </Helmet>
      <PageHeader title={contentUtil.translateToString('pageTitle')} />
      <Wrapper>
        <S.ContactInfo data-testid="messenger-overview">
          <h3>{contentUtil.translateToString('contact.title')}</h3>
          <p>{contentUtil.translateHtml('contact.text')}</p>
          <S.Button
            href={`${process.env.ENV_ZIGGO_URL}/klantenservice/internet-wifi/apps/vodafone-en-ziggo-app`}
            testid="app-download-button">
            {contentUtil.translateToString('contact.button')}
          </S.Button>
        </S.ContactInfo>
        <MessengerContainer />
      </Wrapper>
    </ChildPage>
  );
};

export default Messenger;
